/**
 * Compliance Category Section Component
 * Displays compliance items for a specific category with status management
 */
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import {
  CheckCircle2, Clock, XCircle, Upload, FileText, Download,
  Calendar, Trash2, Eye, MoreVertical, AlertCircle, ChevronDown, ChevronUp
} from "lucide-react";
import { ComplianceFormsPanel } from "./ComplianceEmbeddedDocuments";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status badge configuration
const STATUS_CONFIG = {
  complete: { label: "Complete", color: "bg-green-100 text-green-800 border-green-300", icon: CheckCircle2 },
  expiring: { label: "Expiring", color: "bg-amber-100 text-amber-800 border-amber-300", icon: Clock },
  expired: { label: "Expired", color: "bg-red-100 text-red-800 border-red-300", icon: XCircle },
  missing: { label: "Missing", color: "bg-gray-100 text-gray-800 border-gray-300", icon: AlertCircle },
  not_applicable: { label: "N/A", color: "bg-slate-100 text-slate-600 border-slate-300", icon: null }
};

const ComplianceCategorySection = ({ category, categoryConfig, items = [], onStatusUpdate, centerName = "", organization = {} }) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadNotes, setUploadNotes] = useState("");
  const [uploadExpiration, setUploadExpiration] = useState("");
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [newStatus, setNewStatus] = useState("complete");
  const [statusNotes, setStatusNotes] = useState("");
  const [statusExpiration, setStatusExpiration] = useState("");
  const [showEmbeddedForms, setShowEmbeddedForms] = useState(false);

  const Icon = categoryConfig.icon;

  // Calculate category stats
  const totalItems = items.length;
  const completedItems = items.filter(i => i.complianceStatus === 'complete').length;
  const progress = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  // Handle file upload
  const handleUpload = async () => {
    if (!uploadFile || !selectedItem) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('notes', uploadNotes);
    formData.append('expirationDate', uploadExpiration);

    try {
      await axios.post(`${API}/compliance/status/${selectedItem.id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Document uploaded successfully");
      setUploadDialogOpen(false);
      setUploadFile(null);
      setUploadNotes("");
      setUploadExpiration("");
      onStatusUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  // Handle status update
  const handleStatusUpdate = async () => {
    if (!selectedItem) return;

    try {
      await axios.put(`${API}/compliance/status/${selectedItem.id}`, {
        status: newStatus,
        expirationDate: statusExpiration || null,
        notes: statusNotes
      });
      toast.success("Status updated successfully");
      setStatusDialogOpen(false);
      setNewStatus("complete");
      setStatusNotes("");
      setStatusExpiration("");
      onStatusUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  // Load documents for an item
  const loadDocuments = async (item) => {
    setSelectedItem(item);
    setLoadingDocs(true);
    setDocumentsDialogOpen(true);

    try {
      const response = await axios.get(`${API}/compliance/documents/${item.id}`);
      setDocuments(response.data.documents || []);
    } catch (error) {
      toast.error("Failed to load documents");
      setDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  // Download document
  const downloadDocument = async (itemId, docId, fileName) => {
    try {
      const response = await axios.get(`${API}/compliance/documents/${itemId}/${docId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download document");
    }
  };

  // Delete document
  const deleteDocument = async (itemId, docId) => {
    if (!window.confirm("Are you sure you want to delete this document?")) return;

    try {
      await axios.delete(`${API}/compliance/documents/${itemId}/${docId}`);
      toast.success("Document deleted");
      loadDocuments(selectedItem);
      onStatusUpdate?.();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const openUploadDialog = (item) => {
    setSelectedItem(item);
    setUploadDialogOpen(true);
  };

  const openStatusDialog = (item) => {
    setSelectedItem(item);
    setNewStatus(item.complianceStatus || "missing");
    setStatusNotes(item.statusNotes || "");
    setStatusExpiration(item.expirationDate || "");
    setStatusDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Category Header */}
      <Card className={`${categoryConfig.lightColor} ${categoryConfig.borderColor} border-2`}>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 ${categoryConfig.color} rounded-xl flex items-center justify-center`}>
                <Icon className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">{categoryConfig.label}</h2>
                <p className="text-muted-foreground">
                  {completedItems} of {totalItems} items complete
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-32">
                <Progress value={progress} className="h-3" />
              </div>
              <span className="text-lg font-bold">{progress}%</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Compliance Items Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((item) => {
          const statusConfig = STATUS_CONFIG[item.complianceStatus] || STATUS_CONFIG.missing;
          const StatusIcon = statusConfig.icon;

          return (
            <Card 
              key={item.id} 
              className="hover:shadow-md transition-shadow"
              data-testid={`compliance-item-${item.id}`}
            >
              <CardContent className="pt-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-gray-900">{item.name}</h3>
                      <Badge variant="outline" className="text-xs">
                        Weight: {item.weight}
                      </Badge>
                    </div>
                    
                    {item.description && (
                      <p className="text-sm text-muted-foreground mb-3">{item.description}</p>
                    )}

                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`${statusConfig.color} border gap-1`}>
                        {StatusIcon && <StatusIcon className="w-3 h-3" />}
                        {statusConfig.label}
                      </Badge>
                      
                      {item.expirationDate && (
                        <Badge variant="outline" className="gap-1 text-xs">
                          <Calendar className="w-3 h-3" />
                          Expires: {new Date(item.expirationDate).toLocaleDateString()}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => openUploadDialog(item)}
                    className="gap-1"
                    data-testid={`upload-btn-${item.id}`}
                  >
                    <Upload className="w-4 h-4" />
                    Upload
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => loadDocuments(item)}
                    className="gap-1"
                    data-testid={`view-docs-btn-${item.id}`}
                  >
                    <Eye className="w-4 h-4" />
                    View Docs
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => openStatusDialog(item)}
                    className="gap-1"
                    data-testid={`update-status-btn-${item.id}`}
                  >
                    <MoreVertical className="w-4 h-4" />
                    Status
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Access Forms Panel - Shows embedded forms for applicable items */}
      <ComplianceFormsPanel
        category={category}
        items={items}
        centerName={centerName}
        organization={organization}
        onStatusUpdate={onStatusUpdate}
      />

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>
              Upload a document for: {selectedItem?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="file">Document File</Label>
              <Input
                id="file"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Supported: PDF, JPG, PNG, WebP (max 15MB)
              </p>
            </div>

            <div>
              <Label htmlFor="expiration">Expiration Date (Optional)</Label>
              <Input
                id="expiration"
                type="date"
                value={uploadExpiration}
                onChange={(e) => setUploadExpiration(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={uploadNotes}
                onChange={(e) => setUploadNotes(e.target.value)}
                placeholder="Add any relevant notes..."
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpload} 
              disabled={!uploadFile || uploading}
              className="gap-2"
            >
              {uploading ? "Uploading..." : "Upload Document"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Status</DialogTitle>
            <DialogDescription>
              Update compliance status for: {selectedItem?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="complete">Complete</SelectItem>
                  <SelectItem value="expiring">Expiring Soon</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="missing">Missing</SelectItem>
                  <SelectItem value="not_applicable">Not Applicable</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="status-expiration">Expiration Date</Label>
              <Input
                id="status-expiration"
                type="date"
                value={statusExpiration}
                onChange={(e) => setStatusExpiration(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="status-notes">Notes</Label>
              <Textarea
                id="status-notes"
                value={statusNotes}
                onChange={(e) => setStatusNotes(e.target.value)}
                placeholder="Add any notes..."
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleStatusUpdate}>
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Documents Dialog */}
      <Dialog open={documentsDialogOpen} onOpenChange={setDocumentsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Documents</DialogTitle>
            <DialogDescription>
              Documents for: {selectedItem?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {loadingDocs ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                <p className="text-muted-foreground">Loading documents...</p>
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                <p className="text-muted-foreground">No documents uploaded yet</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => {
                    setDocumentsDialogOpen(false);
                    openUploadDialog(selectedItem);
                  }}
                >
                  Upload Document
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {documents.map((doc) => (
                  <div 
                    key={doc.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="w-8 h-8 text-blue-500" />
                      <div>
                        <p className="font-medium text-sm">{doc.fileName}</p>
                        <p className="text-xs text-muted-foreground">
                          Uploaded {new Date(doc.createdAt).toLocaleDateString()} by {doc.uploadedBy}
                        </p>
                        {doc.notes && (
                          <p className="text-xs text-muted-foreground mt-1">{doc.notes}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => downloadDocument(selectedItem.id, doc.id, doc.fileName)}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => deleteDocument(selectedItem.id, doc.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDocumentsDialogOpen(false)}>
              Close
            </Button>
            <Button onClick={() => {
              setDocumentsDialogOpen(false);
              openUploadDialog(selectedItem);
            }}>
              Upload New
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ComplianceCategorySection;
